import { ButtonName, brandclub_colors } from '@brandclub/common-ui';
import { alpha, styled } from '@mui/material';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SearchIcon } from './SearchIcon';
import { ShopAppButton } from '../../../components/button/ShopAppButton';

const SEARCH_BAR_HEIGHT_MOBILE = 34;
const SEARCH_BAR_HEIGHT_DESKTOP = 44;

const useOutsideClick = (ref: React.RefObject<HTMLElement | null>, onOutside: () => void) => {
  const onOutsideRef = useRef(onOutside);
  onOutsideRef.current = onOutside;
  useEffect(() => {
    function handleMouseDown(event: MouseEvent) {
      const el = ref.current;
      if (el && !el.contains(event.target as Node)) {
        onOutsideRef.current();
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [ref]);
};

const SearchBarContainer = styled('div')<{
  mobile?: boolean;
}>(({ theme, mobile }) => ({
  width: '100%',
  position: 'relative',
  boxSizing: 'border-box',
  maxWidth: 783,
  transition: 'all 0.4s ease-in-out',
  '&.focus': {
    maxWidth: 943,
  },
  '.input_area': {
    height: mobile ? SEARCH_BAR_HEIGHT_MOBILE : SEARCH_BAR_HEIGHT_DESKTOP,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    background: 'inherit',
    borderRadius: SEARCH_BAR_HEIGHT_DESKTOP / 2,
    border: '1px solid ',
    borderColor: '#dcdddc',
    boxShadow: '0 0px 0px 0 rgba(124, 115, 115, 0)',
    transition: 'all 0.4s ease-in-out',
    padding: '0 9.6px 0 16px',
    '.location_search_button': {
      borderRadius: 300,
      fontSize: 14,
      fontWeight: 700,
      padding: '0 16.5px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      transition: 'all 0.4s ease-in-out',
    },
    '.filter_icon': {
      width: 12,
      height: 12,
      objectFit: 'contain',
      transition: 'all 0.4s ease-in-out',
      color: brandclub_colors.gray,
      cursor: 'pointer',
    },
    '.search_icon': {
      width: 18,
      height: 18,
      marginLeft: 8,

      objectFit: 'contain',
      transition: 'all 0.4s ease-in-out',
      marginRight: 15,
    },
    '&.focus': {
      padding: '0 9.6px 0 16px',
      borderColor: '#fff',
      boxShadow: '0 2px 7px 0 rgba(124, 115, 115, 0.16)',

      '.search_icon': {
        width: 18,
        height: 18,
        objectFit: 'contain',
        marginRight: 20,
      },
      '.filter_icon_container': {
        opacity: 0,
      },
    },
    '&.open': {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
      input: {
        '&::placeholder': {
          fontSize: 14,
          fontWeight: 500,
          color: theme.vars.palette.text.primary,
          opacity: 0.3,
        },
      },
    },

    input: {
      marginLeft: 10,
      marginRight: 0,
      marginTop: 0,
      marginBottom: 0,
      flex: 1,
      fontSize: 14,
      fontWeight: 500,
      color: theme.vars.palette.text.primary,
      background: 'none',
      width: '100%',
      outline: 'none',
      border: 'none',
      WebkitTapHighlightColor: 'transparent',
      '&:focus': {
        outline: 'none',
        boxShadow: 'none',
      },
      '&:focus-visible': {
        outline: 'none',
        boxShadow: 'none',
      },

      '&::placeholder': {
        transition: 'all 0.4s ease-in-out',
        fontSize: 14,
        fontWeight: 500,
        color: theme.vars.palette.text.primary,
        opacity: 0.3,
      },
    },
  },
  '.dropdown_area': {
    background: theme.vars.palette.background.paper,
    zIndex: 10,
    position: 'absolute',
    width: '100%',
    minHeight: 0,
    height: 0,
    overflow: 'hidden',

    boxSizing: 'border-box',
    borderRadius: SEARCH_BAR_HEIGHT_DESKTOP / 2,

    // transition: "height 0.4s ease-in-out",
    boxShadow: '0 3px 6px 0 rgba(124, 115, 115, 0.16)',
    display: 'flex',
    flexDirection: 'column',
    listStyleType: 'none',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,

    '.divider': {
      height: 1,
      //   width: "100%",
      transition: 'all 0s',
      opacity: 0,
      margin: '0 15px 10px 15px',
      borderTop: `1px solid ${theme.vars.palette.divider}`,
      boxSizing: 'border-box',
    },
    '&.open': {
      minHeight: 10,
      height: 'auto',
      '.divider': {
        transition: 'all 0.4s',
        opacity: 1,
      },
    },

    '.list': {
      listStyle: 'none',
      padding: '0px 0px 10px 0px',
      marginTop: 0,
      '.list_opt': {
        cursor: 'pointer',

        fontWeight: 500,
        color: theme.vars.palette.text.primary,

        padding: '10px 18px',
        fontSize: 14,
        '&:hover': {
          background: alpha(theme.palette.primary.main, 0.05),
        },
      },
    },
  },
}));

const SearchBar = ({ pinLocation }: { pinLocation: (lat: number, lng: number) => void }) => {
  const [focusInput, setFocusInput] = useState(false);
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [searchInput, setSearchInput] = useState('');

  /** Lazy init: Places may not be on window on first effect tick (loader race). */
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const getSessionToken = (): google.maps.places.AutocompleteSessionToken | null => {
    const places = window.google?.maps?.places;
    if (!places) return null;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  };

  const wrapperRef = useRef<HTMLDivElement>(null);

  const clearFocusUi = useCallback(() => setFocusInput(false), []);
  useOutsideClick(wrapperRef, clearFocusUi);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInput(value);

    const sessionToken = getSessionToken();
    if (value && sessionToken && window.google) {
      const autocompleteService = new window.google.maps.places.AutocompleteService();

      autocompleteService.getPlacePredictions(
        {
          input: value,
          sessionToken,
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setPredictions(predictions);
          } else {
            setPredictions([]);
          }
        },
      );
    } else {
      setPredictions([]);
    }
  };

  const handleSelectPrediction = (prediction: google.maps.places.AutocompletePrediction) => {
    setSearchInput(prediction.description);
    setPredictions([]); // Optionally clear the suggestions.

    // Create a dummy element for the PlacesService.
    const serviceDiv = document.createElement('div');
    const placesService = new window.google.maps.places.PlacesService(serviceDiv);

    // Request the place details, asking specifically for the geometry field.
    placesService.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['geometry'],
      },
      (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place &&
          place.geometry &&
          place.geometry.location
        ) {
          // Retrieve the latitude and longitude.
          const lat = place.geometry.location.lat();
          const lng = place.geometry.location.lng();

          pinLocation(lat, lng);
        } else {
          console.error('Error retrieving place details:', status);
        }
      },
    );
  };

  const openDropdown = focusInput && predictions.length > 0;

  const handleSearchClick = () => {
    let firstPrediction = predictions?.[0];

    if (firstPrediction) {
      handleSelectPrediction(firstPrediction);
    }

    const value = searchInput.trim();
    const sessionToken = getSessionToken();

    if (value && sessionToken && window.google) {
      const autocompleteService = new window.google.maps.places.AutocompleteService();

      autocompleteService.getPlacePredictions(
        {
          input: value,
          sessionToken,
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            handleSelectPrediction(predictions[0]);
          } else {
            setPredictions([]);
          }
        },
      );
    } else {
      setPredictions([]);
    }
  };

  return (
    <SearchBarContainer
      ref={wrapperRef}
      className={`${openDropdown ? 'open' : 'close'} ${focusInput ? 'focus' : 'unfocus'}`}
    >
      <div className={`input_area ${openDropdown ? 'open' : 'close'} ${focusInput ? 'focus' : 'unfocus'}`}>
        <SearchIcon color={brandclub_colors.gray} />
        <input
          placeholder={'Search by city, state or zip code'}
          value={searchInput}
          autoComplete="off"
          onChange={handleInputChange}
          onFocus={() => {
            setFocusInput(true);
          }}
          //   onKeyDown={(e) => {
          //     if (e.key === "Enter") {
          //       onSubmitSearch();
          //     }
          //   }}
        ></input>
        <ShopAppButton
          type="button"
          className="location_search_button"
          height={{ xs: 32 }}
          onClick={handleSearchClick}
          variant="solid"
          color="primary"
          increaseSpecificity
          buttonName={ButtonName.LocationMapSearchButton}
        >
          Search
        </ShopAppButton>
      </div>

      <div className={`dropdown_area ${openDropdown ? 'open' : 'close'}`}>
        <ul className="list">
          <div className="divider"></div>
          {predictions.map((item) => {
            return (
              <li key={item.place_id} className="list_opt" onClick={() => handleSelectPrediction(item)}>
                {item.description}
              </li>
            );
          })}
        </ul>
      </div>
    </SearchBarContainer>
  );
};

export default SearchBar;
